import React, { useEffect, useState } from "react";
import {
  PlayCircle, RotateCcw, LogOut, PhoneCall, Users, CalendarCheck, Moon, DollarSign,
  Bot, User, Cog, ChevronRight, Sparkles, ShieldCheck, MessageSquareText, FileText, CheckCircle2,
} from "lucide-react";
import { PageHeader, MetricCard, Button, Card, CardContent, StatusBadge, FullPageLoader, toast } from "@/components/ds";
import { api, formatApiError } from "@/lib/api";

type Sess = Record<string, any>;

function SimBanner() {
  return (
    <div className="flex items-center gap-2.5 rounded-xl border border-amber-300 bg-amber-50 px-4 py-2.5" data-testid="demo-sim-banner">
      <ShieldCheck className="h-4 w-4 shrink-0 text-amber-600" />
      <p className="text-sm font-medium text-amber-800">
        SIMULATED DEMO — isolated sandbox. No real customers are contacted, no SMS or calls are sent, and no
        production data is created or changed.
      </p>
    </div>
  );
}

const ROLE_STYLE: Record<string, { icon: typeof Bot; cls: string; label: string }> = {
  ai: { icon: Bot, cls: "bg-blue-50 text-blue-800 border-blue-100", label: "Ava (AI)" },
  customer: { icon: User, cls: "bg-slate-50 text-slate-800 border-slate-200", label: "Caller" },
  system: { icon: Cog, cls: "bg-slate-100 text-slate-500 border-slate-200 italic", label: "" },
};

function Transcript({ lines }: { lines: any[] }) {
  return (
    <div className="space-y-2" data-testid="demo-transcript">
      {lines.length === 0 && <p className="py-8 text-center text-sm text-slate-400">Press “Next step” to begin the call.</p>}
      {lines.map((l, i) => {
        const s = ROLE_STYLE[l.role] ?? ROLE_STYLE.system;
        const Icon = s.icon;
        return (
          <div key={i} className={`flex gap-2.5 rounded-lg border px-3 py-2 text-sm ${s.cls} animate-in fade-in slide-in-from-bottom-1`} data-testid={`demo-line-${i}`}>
            <Icon className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{s.label && <strong className="mr-1">{s.label}:</strong>}{l.text}</span>
          </div>
        );
      })}
    </div>
  );
}

export default function Demo() {
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState<Sess | null>(null);
  const [steps, setSteps] = useState<any[]>([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.demoGet();
      setSteps((r.steps as any) ?? []);
      setSession(r.active ? r.session : null);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const start = async () => {
    setBusy(true);
    try { const s = await api.demoStart(); setSteps((s as any).steps ?? []); setSession((s as any).session); toast.success("Demo started"); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const next = async () => {
    setBusy(true);
    try { const r = await api.demoNext(); setSession((r as any).session); if ((r as any).finished) toast.success("Walkthrough complete"); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const reset = async () => {
    setBusy(true);
    try { const s = await api.demoReset(); setSteps((s as any).steps ?? []); setSession((s as any).session); toast.success("Demo reset"); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const exit = async () => {
    setBusy(true);
    try { await api.demoExit(); setSession(null); toast.success("Exited demo"); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  if (loading) return <FullPageLoader />;

  // ---- Landing (not started) ----
  if (!session) {
    return (
      <div data-testid="page-demo" className="space-y-6">
        <PageHeader title="Sales Demo Mode" description="An isolated, fully simulated walkthrough for prospects." testid="demo-header" />
        <SimBanner />
        <Card>
          <CardContent className="flex flex-col items-center gap-5 p-10 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-blue-600 text-white"><Sparkles className="h-8 w-8" /></span>
            <div>
              <h2 className="font-display text-2xl font-semibold text-slate-900">NorthStar Heating &amp; Air</h2>
              <p className="mx-auto mt-2 max-w-lg text-sm text-slate-500">
                Launch a self-contained demo with realistic data and a guided 10-step story showing how the AI
                receptionist answers a call, qualifies a lead and books a job — all simulated.
              </p>
            </div>
            <Button size="lg" onClick={start} disabled={busy} data-testid="demo-start-btn">
              <PlayCircle className="h-5 w-5" /> Start Guided Demo
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ---- Active demo ----
  const g = session.guided ?? {};
  const m = session.metrics ?? {};
  const currentStep = g.current_step ?? 0;
  const finished = currentStep >= (steps.length || 10);

  return (
    <div data-testid="page-demo" className="space-y-6">
      <PageHeader
        title="Sales Demo Mode"
        description={`${session.company} · guided walkthrough`}
        testid="demo-header"
        action={
          <div className="flex gap-2">
            <Button variant="outline" onClick={reset} disabled={busy} data-testid="demo-reset-btn"><RotateCcw className="h-4 w-4" /> Reset Demo</Button>
            <Button variant="ghost" onClick={exit} disabled={busy} data-testid="demo-exit-btn"><LogOut className="h-4 w-4" /> Exit Demo</Button>
          </div>
        }
      />
      <SimBanner />

      {/* Simulated metrics */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5" data-testid="demo-metrics">
        <MetricCard label="Calls answered" value={m.calls_answered} icon={PhoneCall} hint="Simulated" testid="demo-metric-calls" />
        <MetricCard label="Qualified leads" value={m.qualified_leads} icon={Users} hint="Simulated" testid="demo-metric-leads" />
        <MetricCard label="Appointments" value={m.appointments_booked} icon={CalendarCheck} hint="Simulated" testid="demo-metric-appts" />
        <MetricCard label="After-hours opps" value={m.after_hours_opportunities} icon={Moon} hint="Simulated" testid="demo-metric-afterhours" />
        <MetricCard label="Pipeline value" value={`$${Number(m.estimated_pipeline_value ?? 0).toLocaleString()}`} icon={DollarSign} hint="Simulated" testid="demo-metric-pipeline" />
      </div>

      <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
        {/* Guided call */}
        <Card data-testid="demo-guided">
          <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
            <div className="flex items-center gap-2.5">
              <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-blue-50 text-blue-600"><PhoneCall className="h-4 w-4" /></span>
              <h3 className="text-sm font-semibold text-slate-900">Guided call simulation</h3>
            </div>
            <StatusBadge tone="slate" data-testid="demo-step-indicator">Step {Math.min(currentStep, steps.length || 10)} / {steps.length || 10}</StatusBadge>
          </div>
          <CardContent className="space-y-4 p-5">
            <Transcript lines={g.transcript ?? []} />
            {g.sms && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3" data-testid="demo-sms">
                <p className="flex items-center gap-2 text-xs font-semibold text-emerald-700"><MessageSquareText className="h-4 w-4" /> Simulated SMS confirmation (not sent)</p>
                <p className="mt-1 text-sm text-emerald-800">{g.sms.body}</p>
              </div>
            )}
            {g.call_summary && (
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-3" data-testid="demo-summary">
                <p className="flex items-center gap-2 text-xs font-semibold text-blue-700"><FileText className="h-4 w-4" /> Call summary (simulated)</p>
                <p className="mt-1 text-sm text-blue-800">{g.call_summary.summary}</p>
              </div>
            )}
            <div className="flex items-center justify-between border-t border-slate-100 pt-4">
              <p className="text-sm text-slate-500">{finished ? "Walkthrough complete." : (steps[currentStep]?.narration ?? "")}</p>
              {finished ? (
                <Button variant="outline" onClick={reset} disabled={busy} data-testid="demo-replay-btn"><RotateCcw className="h-4 w-4" /> Replay</Button>
              ) : (
                <Button onClick={next} disabled={busy} data-testid="demo-next-btn">Next step <ChevronRight className="h-4 w-4" /></Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Step list + pipeline */}
        <div className="space-y-6">
          <Card data-testid="demo-steps">
            <div className="border-b border-slate-100 px-5 py-4"><h3 className="text-sm font-semibold text-slate-900">Presentation steps</h3></div>
            <CardContent className="p-2">
              <ol className="space-y-0.5">
                {(steps.length ? steps : Array.from({ length: 10 })).map((st: any, i: number) => {
                  const done = i < currentStep;
                  const active = i === currentStep && !finished;
                  return (
                    <li key={i} className={`flex items-center gap-3 rounded-lg px-3 py-2 ${active ? "bg-blue-50" : ""}`} data-testid={`demo-step-${i}`}>
                      <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${done ? "bg-emerald-100 text-emerald-600" : active ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-400"}`}>
                        {done ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
                      </span>
                      <span className={`text-sm ${active ? "font-medium text-blue-700" : done ? "text-slate-600" : "text-slate-400"}`}>{st?.title ?? `Step ${i + 1}`}</span>
                    </li>
                  );
                })}
              </ol>
            </CardContent>
          </Card>

          <Card data-testid="demo-pipeline">
            <div className="border-b border-slate-100 px-5 py-4"><h3 className="text-sm font-semibold text-slate-900">Lead pipeline (simulated)</h3></div>
            <CardContent className="divide-y divide-slate-100 p-0">
              {(session.leads ?? []).slice(0, 5).map((l: any) => (
                <div key={l.id} className="flex items-center justify-between px-5 py-3" data-testid={`demo-lead-${l.id}`}>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-slate-900">{l.name}</p>
                    <p className="text-xs text-slate-500">{l.service} · {l.stage}</p>
                  </div>
                  <span className="text-sm font-semibold text-slate-700">${Number(l.value).toLocaleString()}</span>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
